import React from 'react';
import { Phone, Mail, MapPin, Link as LinkIcon } from 'lucide-react';
import { sectionTitles, defaultPlaceholderData } from '../common/resumeConstants';
import { 
  checkHasRealData, 
  getCurrentData, 
  getPaperTypeText
} from '../common/resumeHelpers';
import { 
  ContactInfo,
  SummarySection,
  CoreCompetenciesSection,
  ExperienceSection,
  getPlaceholderClass,
  renderBulletPoints
} from '../common/ResumeComponents';

// Template 10 - 파격적인 블랙톤 디자인 with 초록색 포인트
const Template10 = ({ resumeData, updatingField, language = 'kr' }) => {
  const titles = sectionTitles[language] || sectionTitles.kr;
  const hasRealData = checkHasRealData(resumeData);
  const currentData = getCurrentData(resumeData, defaultPlaceholderData, hasRealData);

  return (
    <div className={`template-10 resume-container bg-black text-white ${language === 'en' ? 'resume-english' : ''}`}>
      {/* Header with green accent */}
      <div className="relative bg-black border-b-4 border-green-400">
        <div className="px-8 py-10">
          <h1 className={getPlaceholderClass(hasRealData.name, "text-4xl font-bold mb-2 text-white", "text-4xl font-bold mb-2 text-gray-500 italic")}>
            {currentData.name}
          </h1>
          <p className={getPlaceholderClass(hasRealData.title, "text-xl mb-6 text-green-400", "text-xl mb-6 text-gray-500 italic")}>
            {currentData.title}
          </p>
          
          {/* Contact Info */}
          <div className="flex flex-wrap gap-4 text-sm">
            {currentData.phone && (
              <div className={`flex items-center gap-2 ${hasRealData.phone ? "text-gray-300" : "text-gray-600 italic"}`}>
                <Phone className="w-4 h-4" />
                <span>{currentData.phone}</span>
              </div>
            )}
            {currentData.email && (
              <div className={`flex items-center gap-2 ${hasRealData.email ? "text-gray-300" : "text-gray-600 italic"}`}>
                <Mail className="w-4 h-4" />
                <span>{currentData.email}</span>
              </div>
            )}
            {currentData.address && (
              <div className={`flex items-center gap-2 ${hasRealData.address ? "text-gray-300" : "text-gray-600 italic"}`}>
                <MapPin className="w-4 h-4" />
                <span>{currentData.address}</span>
              </div>
            )}
            {currentData.socialLinks && currentData.socialLinks.map((social, index) => (
              <div key={index} className={`flex items-center gap-2 ${hasRealData.socialLinks ? "text-gray-300" : "text-gray-600 italic"}`}>
                <LinkIcon className="w-4 h-4" />
                <span>{social.url.replace(/^https?:\/\/(www\.)?/, '')}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="px-8 py-8">
        {/* Summary */}
        {currentData.summary && (
          <section className="mb-8">
            <h2 className="text-lg font-bold uppercase mb-4 text-green-400 tracking-wider">
              {titles.introduction}
            </h2>
            <p className={hasRealData.summary ? "text-base leading-relaxed text-gray-300" : "text-base leading-relaxed text-gray-600 italic"}>
              {currentData.summary}
            </p>
          </section>
        )}

        {/* Core Competencies */}
        {currentData.keyStrengths.length > 0 && (
          <section className="mb-8">
            <h2 className="text-lg font-bold uppercase mb-4 text-green-400 tracking-wider">
              {titles.coreCompetencies}
            </h2>
            <div className="space-y-2">
              {currentData.keyStrengths.map((strength, index) => (
                <div key={index} className="flex items-start">
                  <span className={hasRealData.keyStrengths ? "text-green-400 mt-1 mr-2" : "text-gray-600 mt-1 mr-2"}>▸</span>
                  <span className={hasRealData.keyStrengths ? "text-base leading-relaxed text-gray-300" : "text-base leading-relaxed text-gray-600 italic"}>
                    {strength}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Professional Experience */}
        <section className="mb-8">
          <h2 className="text-lg font-bold uppercase mb-6 text-green-400 tracking-wider">
            {titles.experience}
          </h2>
          {currentData.experiences.map((exp, index) => (
            <div key={index} className="mb-8 last:mb-0 border-l-2 border-green-400 pl-6">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className={`text-lg font-bold ${!hasRealData.experiences ? 'text-gray-500 italic' : 'text-white'}`}>
                    {exp.position}
                  </h3>
                  <p className={`text-base ${!hasRealData.experiences ? 'text-gray-500 italic' : 'text-green-400'}`}>
                    {exp.company}
                  </p>
                </div>
                <div className={`text-sm ${!hasRealData.experiences ? 'text-gray-500 italic' : 'text-gray-300'}`}>
                  {exp.startDate} - {exp.endDate}
                </div>
              </div>
              
              {exp.overallDescription && (
                <div className="mt-3">
                  {exp.overallDescription && exp.overallDescription.split('\n').map((line, i) => (
                    <div key={i} className="flex items-start mb-1.5">
                      <span className={hasRealData.experiences ? "text-gray-300 mr-2" : "text-gray-600 mr-2"}>•</span>
                      <span className={hasRealData.experiences ? "text-gray-300" : "text-gray-600 italic"}>{line}</span>
                    </div>
                  ))}
                </div>
              )}

              {exp.projects && exp.projects.length > 0 && (
                <div className="mt-4 space-y-4">
                  {exp.projects.map((project, projIndex) => (
                    <div key={projIndex} className="pl-6">
                      <h4 className={`font-semibold mb-2 flex items-center ${!hasRealData.experiences ? 'text-gray-500 italic' : 'text-green-400'}`}>
                        <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                        {project.projectName}
                      </h4>
                      {project.projectDescription && (
                        <div className="pl-5 text-sm">
                          {project.projectDescription.split('\n').map((line, lineIndex) => (
                            <div key={lineIndex} className="flex items-start mb-1">
                              <span className={hasRealData.experiences ? "text-gray-300 mr-2" : "text-gray-600 mr-2"}>-</span>
                              <span className={hasRealData.experiences ? "text-gray-300" : "text-gray-600 italic"}>{line}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </section>

        {/* Two Column Section */}
        <div className="grid grid-cols-2 gap-8">
          {/* Left Column */}
          <div>
            {/* Education */}
            <section className="mb-8">
              <h2 className="text-lg font-bold uppercase mb-4 text-green-400 tracking-wider">
                {titles.education}
              </h2>
              {currentData.education.map((edu, index) => (
                <div key={index} className="mb-4 last:mb-0 flex justify-between items-start">
                  <div>
                    <h3 className={hasRealData.education ? "font-bold text-white" : "font-bold text-gray-600 italic"}>
                      {edu.degree}
                    </h3>
                    <p className={hasRealData.education ? "text-sm text-green-400" : "text-sm text-gray-600 italic"}>
                      {edu.school}
                    </p>
                    {edu.major && (
                      <p className={hasRealData.education ? "text-sm text-gray-400" : "text-sm text-gray-600 italic"}>
                        {edu.major}
                      </p>
                    )}
                  </div>
                  <div className={hasRealData.education ? "text-sm text-gray-400" : "text-sm text-gray-600 italic"}>
                    {edu.startDate} - {edu.endDate}
                  </div>
                </div>
              ))}
            </section>

            {/* Skills */}
            {currentData.skills.length > 0 && (
              <section>
                <h2 className="text-lg font-bold uppercase mb-4 text-green-400 tracking-wider">
                  {titles.skills}
                </h2>
                <div className="flex flex-wrap gap-2">
                  {currentData.skills.map((skill, index) => (
                    <span
                      key={index}
                      className={hasRealData.skills ? "px-3 py-1 text-sm border text-black bg-green-400 border-green-400 font-medium" : "px-3 py-1 text-sm border text-gray-600 bg-gray-800 border-gray-600 font-medium italic"}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Right Column */}
          <div>
            {/* Certificates */}
            {currentData.certificates.length > 0 && (
              <section className="mb-8">
                <h2 className="text-lg font-bold uppercase mb-4 text-green-400 tracking-wider">
                  {titles.certificates}
                </h2>
                {currentData.certificates.map((cert, index) => (
                  <div key={index} className="mb-3 last:mb-0">
                    <h3 className={hasRealData.certificates ? "font-semibold text-white" : "font-semibold text-gray-600 italic"}>
                      {cert.name}
                    </h3>
                    <p className={hasRealData.certificates ? "text-sm text-gray-400" : "text-sm text-gray-600 italic"}>
                      {cert.issuer} • {cert.date}
                    </p>
                  </div>
                ))}
              </section>
            )}

            {/* Papers */}
            {hasRealData.papers && (
              <section>
                <h2 className="text-lg font-bold uppercase mb-4 text-green-400 tracking-wider">
                  {titles.papers}
                </h2>
                {currentData.papers.map((paper, index) => (
                  <div key={index} className="mb-4 last:mb-0">
                    <h3 className={hasRealData.papers ? "font-semibold text-white" : "font-semibold text-gray-600 italic"}>
                      {paper.title}
                    </h3>
                    <p className={hasRealData.papers ? "text-sm text-gray-400" : "text-sm text-gray-600 italic"}>
                      {paper.authors}
                    </p>
                    <p className={hasRealData.papers ? "text-sm text-gray-400" : "text-sm text-gray-600 italic"}>
                      {paper.journal}, {paper.year}
                      {paper.type && (
                        <span className="ml-2 px-2 py-0.5 bg-green-900 text-green-400 text-xs">
                          {getPaperTypeText(paper.type)}
                        </span>
                      )}
                    </p>
                  </div>
                ))}
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Template10;